import React from 'react';

export type ColumnsCount = 2 | 3 | 4;

export type ColumnsLayoutProps = {
  columnsCount?: ColumnsCount | null;
  columnsGap?: number | null;
  contentAlignment?: 'top' | 'middle' | 'bottom' | null;
  fixedWidths?: [number | null | undefined, number | null | undefined, number | null | undefined, number | null | undefined] | null;
};

type ColumnsContainerLayoutProps = {
  style?: {
    backgroundColor?: string | null;
    padding?: { top: number; bottom: number; right: number; left: number } | null;
  } | null;
  props?: ColumnsLayoutProps | null;
  columns: React.ReactNode[];
};

const DEFAULTS: Required<ColumnsLayoutProps> = {
  columnsCount: 2,
  columnsGap: 0,
  contentAlignment: 'middle',
  fixedWidths: null,
};

const MAX_COLUMNS = 4;

function getPadding(
  padding?: { top: number; bottom: number; right: number; left: number } | null
) {
  return padding ? `${padding.top}px ${padding.right}px ${padding.bottom}px ${padding.left}px` : undefined;
}

function getPaddingBefore(index: number, columnsGap: number, columnsCount: number) {
  if (index === 0) {
    return 0;
  }
  return (index * columnsGap) / columnsCount;
}

function getPaddingAfter(index: number, columnsGap: number, columnsCount: number) {
  if (index === columnsCount - 1) {
    return 0;
  }
  return ((columnsCount - 1 - index) * columnsGap) / columnsCount;
}

function TableCell({
  index,
  layoutProps,
  children,
}: {
  index: number;
  layoutProps: Required<ColumnsLayoutProps>;
  children: React.ReactNode;
}) {
  const { columnsCount, columnsGap, contentAlignment, fixedWidths } = layoutProps;

  if (index >= columnsCount) {
    return null;
  }

  const fixedWidth = fixedWidths?.[index];
  const style: React.CSSProperties = {
    boxSizing: 'border-box',
    verticalAlign: contentAlignment ?? 'middle',
    paddingLeft: getPaddingBefore(index, columnsGap ?? 0, columnsCount ?? 2),
    paddingRight: getPaddingAfter(index, columnsGap ?? 0, columnsCount ?? 2),
    width: fixedWidth != null ? `${fixedWidth}px` : undefined,
  };

  return <td style={style}>{children}</td>;
}

export default function ColumnsContainerLayout({ style, props, columns }: ColumnsContainerLayoutProps) {
  const layoutProps: Required<ColumnsLayoutProps> = {
    columnsCount: props?.columnsCount ?? DEFAULTS.columnsCount,
    columnsGap: props?.columnsGap ?? DEFAULTS.columnsGap,
    contentAlignment: props?.contentAlignment ?? DEFAULTS.contentAlignment,
    fixedWidths: props?.fixedWidths ?? DEFAULTS.fixedWidths,
  };

  const wrapperStyle: React.CSSProperties = {
    backgroundColor: style?.backgroundColor ?? undefined,
    padding: getPadding(style?.padding),
  };

  return (
    <div style={wrapperStyle}>
      <table
        align="center"
        width="100%"
        cellPadding={0}
        border={0}
        style={{ tableLayout: 'fixed', borderCollapse: 'collapse' }}
      >
        <tbody style={{ width: '100%' }}>
          <tr style={{ width: '100%' }}>
            {Array.from({ length: MAX_COLUMNS }, (_, index) => (
              <TableCell key={index} index={index} layoutProps={layoutProps}>
                {columns[index] ?? null}
              </TableCell>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
